import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { ShieldCheck, Plus, X, Loader2, Save, AlertTriangle } from 'lucide-react';

/**
 * Sign-up Policy card (Settings → Security).
 *
 * Admins here can:
 *   * Switch Google sign-ups between "allowlist" and "open"
 *   * Manage the allowed emails / allowed domains for the allowlist mode
 *   * Toggle the direct /auth/register endpoint on or off
 *
 * The backend already guarantees personaltouchgh@gmail.com stays on the
 * allowlist even if an admin removes them here, so the owner can never
 * accidentally lock themselves out. Existing users always keep working —
 * the allowlist only gates NEW account creation.
 */
export default function SignupPolicyCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [policy, setPolicy] = useState({
    google_mode: 'allowlist',
    allowed_domains: [],
    allowed_emails: [],
    allow_registration: false,
  });
  const [emailDraft, setEmailDraft] = useState('');
  const [domainDraft, setDomainDraft] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/auth/signup-policy');
      setPolicy(prev => ({ ...prev, ...(res.data || {}) }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load sign-up policy.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isAllowlist = policy.google_mode === 'allowlist';

  const patchAndSave = async (patch) => {
    setSaving(true);
    try {
      const res = await api.put('/auth/signup-policy', patch);
      setPolicy(prev => ({ ...prev, ...res.data }));
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update policy.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const addEmail = async () => {
    const e = emailDraft.trim().toLowerCase();
    if (!e) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      toast.error('Enter a valid email address.');
      return;
    }
    if (policy.allowed_emails.includes(e)) {
      toast.info(`${e} is already on the allowlist.`);
      setEmailDraft('');
      return;
    }
    const ok = await patchAndSave({ allowed_emails: [...policy.allowed_emails, e] });
    if (ok) { setEmailDraft(''); toast.success(`Added ${e}`); }
  };

  const removeEmail = async (e) => {
    if (e === 'personaltouchgh@gmail.com') {
      toast.warning("The owner account can't be removed from the allowlist.");
      return;
    }
    await patchAndSave({ allowed_emails: policy.allowed_emails.filter(x => x !== e) });
  };

  const addDomain = async () => {
    const d = domainDraft.trim().toLowerCase().replace(/^@/, '');
    if (!d) return;
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(d)) {
      toast.error("Enter a valid domain (e.g. 'personaltouch.com').");
      return;
    }
    if (policy.allowed_domains.includes(d)) {
      toast.info(`${d} is already on the allowlist.`);
      setDomainDraft('');
      return;
    }
    const ok = await patchAndSave({ allowed_domains: [...policy.allowed_domains, d] });
    if (ok) { setDomainDraft(''); toast.success(`Added ${d}`); }
  };

  const removeDomain = async (d) => {
    await patchAndSave({ allowed_domains: policy.allowed_domains.filter(x => x !== d) });
  };

  const toggleMode = async (nextIsAllowlist) => {
    await patchAndSave({ google_mode: nextIsAllowlist ? 'allowlist' : 'open' });
  };

  const toggleRegistration = async (next) => {
    await patchAndSave({ allow_registration: !!next });
  };

  return (
    <Card data-testid="signup-policy-card">
      <CardHeader>
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" /> Sign-up Policy
        </CardTitle>
        <CardDescription>
          Control who is allowed to create new accounts. Existing users always
          keep signing in — this only gates new account creation.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading policy…
          </div>
        ) : (
          <>
            {/* Google mode toggle */}
            <div className="flex items-start justify-between gap-4 p-3 rounded-lg border">
              <div>
                <p className="text-sm font-medium">Restrict Google sign-ups to allowlist</p>
                <p className="text-xs text-muted-foreground mt-1">
                  When on, only Google accounts whose email or domain is on the
                  allowlist can create new accounts. When off, any Google user
                  can sign up as a cashier.
                </p>
              </div>
              <Switch
                checked={isAllowlist}
                onCheckedChange={toggleMode}
                disabled={saving}
                data-testid="signup-policy-allowlist-toggle"
              />
            </div>

            {/* Direct registration toggle */}
            <div className="flex items-start justify-between gap-4 p-3 rounded-lg border">
              <div>
                <p className="text-sm font-medium">Allow direct email + password sign-ups</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Enables <code>POST /api/auth/register</code>. Keep off unless
                  you're bulk-loading accounts — new users should normally be
                  added from the Users page.
                </p>
              </div>
              <Switch
                checked={!!policy.allow_registration}
                onCheckedChange={toggleRegistration}
                disabled={saving}
                data-testid="signup-policy-registration-toggle"
              />
            </div>

            {/* Allowed emails */}
            <div className={`space-y-2 ${!isAllowlist ? 'opacity-50 pointer-events-none' : ''}`}>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Allowed emails
              </Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={emailDraft}
                  onChange={e => setEmailDraft(e.target.value)}
                  placeholder="user@example.com"
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                  data-testid="signup-policy-email-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addEmail}
                  disabled={saving || !emailDraft.trim()}
                  data-testid="signup-policy-email-add"
                  className="gap-1"
                >
                  <Plus className="h-3.5 w-3.5" /> Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="signup-policy-emails-list">
                {policy.allowed_emails.length === 0 && (
                  <p className="text-xs text-muted-foreground">No emails yet.</p>
                )}
                {policy.allowed_emails.map(e => {
                  const isOwner = e === 'personaltouchgh@gmail.com';
                  return (
                    <Badge key={e} variant="secondary" className="gap-1 pl-2 pr-1">
                      <span className="text-xs">{e}</span>
                      {isOwner ? (
                        <span
                          className="ml-1 text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-400"
                          title="Owner account — cannot be removed"
                        >
                          owner
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => removeEmail(e)}
                          disabled={saving}
                          className="ml-0.5 rounded hover:bg-muted p-0.5"
                          aria-label={`Remove ${e}`}
                          data-testid={`signup-policy-email-remove-${e}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </Badge>
                  );
                })}
              </div>
            </div>

            {/* Allowed domains */}
            <div className={`space-y-2 ${!isAllowlist ? 'opacity-50 pointer-events-none' : ''}`}>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Allowed domains
              </Label>
              <div className="flex gap-2">
                <Input
                  value={domainDraft}
                  onChange={e => setDomainDraft(e.target.value)}
                  placeholder="personaltouch.com"
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addDomain(); } }}
                  data-testid="signup-policy-domain-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addDomain}
                  disabled={saving || !domainDraft.trim()}
                  data-testid="signup-policy-domain-add"
                  className="gap-1"
                >
                  <Plus className="h-3.5 w-3.5" /> Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="signup-policy-domains-list">
                {policy.allowed_domains.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No domains yet — add e.g. <code>personaltouch.com</code> to
                    allow every Google account under that domain.
                  </p>
                )}
                {policy.allowed_domains.map(d => (
                  <Badge key={d} variant="secondary" className="gap-1 pl-2 pr-1">
                    <span className="text-xs">@{d}</span>
                    <button
                      type="button"
                      onClick={() => removeDomain(d)}
                      disabled={saving}
                      className="ml-0.5 rounded hover:bg-muted p-0.5"
                      aria-label={`Remove ${d}`}
                      data-testid={`signup-policy-domain-remove-${d}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {isAllowlist && policy.allowed_emails.length === 0 && policy.allowed_domains.length === 0 && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-800 dark:text-amber-300">
                  You&apos;re in allowlist mode with no entries — new Google
                  users will be blocked. Add at least one email or domain, or
                  turn off allowlist mode.
                </p>
              </div>
            )}

            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <Save className="h-3 w-3" />
              Changes save automatically as you toggle switches or add / remove entries.
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
